import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Settings, Check } from 'lucide-react';
import { CONSENT_CATEGORIES } from '../context/CookieConsentContext';

export default function CookieConsent({
  darkMode,
  showBanner,
  onAcceptAll,
  onAcceptNecessary,
  onAcceptSelected,
  onClose,
}) {
  const [showPreferences, setShowPreferences] = useState(false);
  const [selections, setSelections] = useState({
    functional: true,
    analytics: false,
  });

  if (!showBanner) return null;

  const handleToggle = (category) => {
    if (category === 'necessary') return; // Can't toggle necessary
    setSelections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSavePreferences = () => {
    onAcceptSelected(selections);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />

      {/* Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[9999] ${
          darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
        } border-t shadow-2xl`}
      >
        <div className="max-w-6xl mx-auto p-6">
          {!showPreferences ? (
            /* Simple Banner View */
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <h3
                  className={`text-lg font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  We value your privacy
                </h3>
                <p
                  className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  We use cookies and similar technologies to enhance your browsing experience,
                  remember your preferences, and analyze site traffic. By clicking "Accept All",
                  you consent to our use of cookies. Read our{' '}
                  <Link
                    to="/cookies"
                    className="underline hover:opacity-70"
                    onClick={onClose}
                  >
                    Cookie Policy
                  </Link>{' '}
                  for more information.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowPreferences(true)}
                  className={`px-5 py-2.5 text-sm font-medium border transition-colors flex items-center justify-center gap-2 ${
                    darkMode
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Settings size={16} />
                  Preferences
                </button>
                <button
                  onClick={onAcceptNecessary}
                  className={`px-5 py-2.5 text-sm font-medium border transition-colors ${
                    darkMode
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Necessary Only
                </button>
                <button
                  onClick={onAcceptAll}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                    darkMode
                      ? 'bg-white text-black hover:bg-gray-100'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            /* Preferences View */
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3
                  className={`text-lg font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className={`p-2 hover:opacity-70 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {Object.values(CONSENT_CATEGORIES).map((category) => (
                  <div
                    key={category.id}
                    className={`p-4 border ${
                      darkMode ? 'border-gray-800' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4
                          className={`font-medium ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {category.name}
                          {category.required && (
                            <span
                              className={`ml-2 text-xs ${
                                darkMode ? 'text-gray-500' : 'text-gray-400'
                              }`}
                            >
                              (Required)
                            </span>
                          )}
                        </h4>
                        <p
                          className={`text-sm mt-1 ${
                            darkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          {category.description}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggle(category.id)}
                        disabled={category.required}
                        className={`ml-4 w-12 h-6 rounded-full transition-colors relative ${
                          category.required || selections[category.id] || category.id === 'necessary'
                            ? darkMode
                              ? 'bg-white'
                              : 'bg-black'
                            : darkMode
                            ? 'bg-gray-700'
                            : 'bg-gray-300'
                        } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 rounded-full transition-transform ${
                            category.required || selections[category.id] || category.id === 'necessary'
                              ? 'translate-x-7'
                              : 'translate-x-1'
                          } ${
                            category.required || selections[category.id] || category.id === 'necessary'
                              ? darkMode
                                ? 'bg-black'
                                : 'bg-white'
                              : darkMode
                              ? 'bg-gray-400'
                              : 'bg-white'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={onAcceptNecessary}
                  className={`px-5 py-2.5 text-sm font-medium border transition-colors ${
                    darkMode
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Reject All
                </button>
                <button
                  onClick={handleSavePreferences}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    darkMode
                      ? 'bg-white text-black hover:bg-gray-100'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  <Check size={16} />
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
